import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { Badge } from './ui/badge'
import { Avatar, AvatarFallback } from './ui/avatar'
import { MoreVertical, Search, UserX } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Button } from './ui/button'
import { Input } from './ui/input'

interface UserData {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  role: string | null
  created_at: string
}

interface UsersClientProps {
  users: UserData[]
}

export default function AdminUsersClient({ users: initialUsers }: UsersClientProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const filteredUsers = useMemo(() => {
    return initialUsers.filter(user => {
      const name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      return name.includes(lowercasedSearchTerm) || email.includes(lowercasedSearchTerm);
    });
  }, [initialUsers, searchTerm]);

  const getInitials = (firstName: string | null, lastName: string | null) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
  }

  return (
    <div className="bg-white dark:bg-gray-900 border rounded-lg dark:border-gray-800 shadow-sm">
      <div className="p-4 border-b dark:border-gray-800">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
      </div>
      {filteredUsers.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map(user => (
              <TableRow key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <TableCell>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 dark:bg-gray-700 text-blue-600 dark:text-gray-200 font-semibold">
                        {getInitials(user.first_name, user.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-50">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={user.role === 'admin' ? 'destructive' : 'success'}>{user.role}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/users/${user.id}`)}>
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem>Make Admin</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-20">
          <UserX className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-2xl font-semibold text-gray-800 dark:text-white">No Users Found</h3>
          <p className="text-muted-foreground mt-2">Your search for "{searchTerm}" did not match any users.</p>
        </div>
      )}
    </div>
  )
} 